import { Link } from 'react-router-dom';
import { Card, Image, Badge, Group, Text, Stack, AspectRatio } from '@mantine/core';
import { IconBook, IconBuildingChurch, IconCategory, IconClock } from '@tabler/icons-react';
import { RichTextPreview } from '@/components/common/RichText';
import { VisibilityBadge } from '@/components/common/VisibilityBadge';
import { courseHref } from '@/utils/slug';

/**
 * Loose shape — accepts both the bare <c>Course</c> and the enriched
 * <c>CourseModelView</c>. Only fields the card actually renders are listed;
 * the enriched-view extras are optional so a bare <c>Course</c> still fits.
 */
export interface CourseCardCourse {
  courseID: number;
  name?: string | null;
  description?: string | null;
  thumbnail?: string | null;
  color?: string | null;
  slug?: string | null;
  /** Enriched-view extras — used for the restricted-course badge + meta line. */
  sessionCount?: number | null;
  totalDurationInSeconds?: number | null;
  visibilityName?: string | null;
  visibilityColor?: string | null;
}

export interface CourseCardProps {
  course: CourseCardCourse;
  /** Pass through from the enriched ModelView when available. */
  categoryName?: string | null;
  categoryColor?: string | null;
  ministryName?: string | null;
}

/** Human-readable runtime, e.g. "45 min" or "1h 20m". Null when unknown. */
function formatDuration(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 1) return '< 1 min';
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Course card styled like Coursera/Udemy — big thumbnail, a Ministry chip and
 * a Category chip so users see at-a-glance where the course fits.
 *
 * When the course isn't <c>Public</c> visibility we add a small lock badge so
 * the viewer knows it's gated (sign-in / role / right) before they click
 * through. The actual access gate is still enforced on the detail page.
 */
export function CourseCard({ course, categoryName, categoryColor, ministryName }: CourseCardProps) {
  // Slug-only nav. When the entity has no persisted slug we kebab-case the
  // name client-side; the backend resolver does the same fallback so the
  // round-trip succeeds either way.
  const to = courseHref(course);

  // The shared VisibilityBadge handles Unlisted (link icon, amber) vs
  // Restricted (lock, grape) and renders nothing for Public — so we don't
  // need a local `isRestricted` flag here.
  const sessionCount = course.sessionCount ?? 0;
  const duration = formatDuration(course.totalDurationInSeconds);
  const meta = [
    sessionCount > 0 ? `${sessionCount} ${sessionCount === 1 ? 'session' : 'sessions'}` : null,
    duration,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card
      component={Link}
      to={to}
      shadow="sm"
      padding="md"
      radius="lg"
      withBorder
      style={{
        textDecoration: 'none',
        color: 'inherit',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Card.Section>
        <AspectRatio ratio={16 / 9}>
          {course.thumbnail ? (
            <Image
              src={course.thumbnail}
              alt={course.name ?? 'Course'}
              fit="cover"
              fallbackSrc="/course-fallback.svg"
            />
          ) : (
            <div
              style={{
                background: course.color
                  ? `linear-gradient(135deg, ${course.color}aa, var(--mantine-color-brand-6))`
                  : 'linear-gradient(135deg, var(--mantine-color-brand-5), var(--mantine-color-brand-7))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconBook size={48} color="white" opacity={0.7} />
            </div>
          )}
        </AspectRatio>
      </Card.Section>

      <Stack gap="xs" mt="md" style={{ flex: 1 }}>
        <Group gap={6} wrap="wrap">
          {ministryName && (
            <Badge
              variant="light"
              color="brand"
              size="sm"
              leftSection={<IconBuildingChurch size={10} />}
            >
              {ministryName}
            </Badge>
          )}
          {categoryName && (
            <Badge
              variant="light"
              color={categoryColor ?? 'gray'}
              size="sm"
              leftSection={<IconCategory size={10} />}
            >
              {categoryName}
            </Badge>
          )}
          <VisibilityBadge
            visibilityName={course.visibilityName}
            visibilityColor={course.visibilityColor}
          />
        </Group>

        <Text fw={600} lineClamp={2} size="md">
          {course.name ?? 'Untitled course'}
        </Text>
        <RichTextPreview html={course.description} size="sm" c="dimmed" lineClamp={2} />

        {meta && (
          <Group gap={5} wrap="nowrap" mt="auto" pt={4} c="dimmed">
            <IconClock size={14} />
            <Text size="xs">{meta}</Text>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
